import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getPurchasedExams, getPurchasedProducts } from "@helpers/user";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useCache from "@hooks/useCache";
import useCacheKey from "@hooks/useCacheKey";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import type { ExamDetails } from "@memorang/types";
import AppContextBanner from "@memorang/ui/components/AppContextBanner";
import { useToast } from "@memorang/ui/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import { useState } from "react";

const AppContextBannerContainer = () => {
	const { appContextCacheKey } = useCacheKey();

	const {
		data: appContextBanner,
		isLoading,
		setCache,
	} = useCache<{
		[examId: string]: {
			shouldShowBanner: boolean;
		};
	}>({
		key: appContextCacheKey || "",
		defaultData: {},
		enabled: true,
		staleTime: 0,
		shouldInvalidate: true,
		shouldOptimisticUpdate: true,
	});
	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

	const { showToast } = useToast();

	const { explicitCallTrackCustomEvent, trackCustomEvent } = useAnalytics(
		undefined,
		false,
	);

	const appFont = useAppStore((state) => state.app.font);

	const client = useQueryClient();
	const {
		handleSwitchExamContext,
		currentExamContext: { examId, examName: currentExamName },
	} = useExamContext();

	const { handleExamsFilterNavigation } = useCustomNavigation();

	if (isLoading) {
		return null;
	}

	const cachedExams = client.getQueryData<ExamDetails[]>([cacheKeys.exams]);
	const purchasedExams = getPurchasedExams(cachedExams ?? [])?.filter(
		(exam) => typeof examId === "string" && exam.exam.id !== examId,
	);
	const hasMoreThanOneExam = purchasedExams?.length > 1;

	const getBannerTitle = (): string | null => {
		if (!purchasedExams?.length) {
			return null;
		}

		const purchasedProducts = getPurchasedProducts(purchasedExams);
		const productTexts = purchasedProducts.map(
			(product) => `${product.bundle.name} (**${product.bundle.examName}**)`,
		);

		if (!productTexts.length) {
			return null;
		}

		const baseMessage = `Your current exam is set to **${currentExamName}**. You also have access to: `;

		if (hasMoreThanOneExam && productTexts.length > 1) {
			const lastProduct = productTexts.pop();
			return `${baseMessage}${productTexts.join(", ")} and ${lastProduct}.`;
		}

		return `${baseMessage}${productTexts.join(", ")}.`;
	};

	const getBannerCta = (): string => {
		if (purchasedExams?.length === 1) {
			return `Switch to ${purchasedExams[0].exam.name}`;
		}
		return "Switch exam";
	};

	const bannerTitle = getBannerTitle();
	const cta = getBannerCta();

	const visible = Boolean(
		appContextBanner?.[examId]?.shouldShowBanner == null && bannerTitle,
	);

	if (!bannerTitle) {
		return null;
	}

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};

	const handleSwitchExam = () => {
		handleExamsFilterNavigation(toggleShowExamsFilterDialog);
	};

	const handlePostExamContextSwitchCallback = (newExamId: string) => {
		setCache({ [newExamId]: { shouldShowBanner: false } });
		const newExam = cachedExams?.find((exam) => exam.exam.id === newExamId);
		if (newExam) {
			trackCustomEvent({
				eventName: events.appContextBannerExamContextSwitched,
				exam_id: newExam.exam.id,
				exam_name: newExam.exam.name,
			});
		}
	};

	const handleSwitchToSpecificExamContext = async () => {
		const examDetails = purchasedExams[0];
		if (examDetails) {
			const message = `Switched Exam from "${currentExamName}" to "${examDetails.exam.name}"`;
			showToast({ message, duration: 500 });
			await handleSwitchExamContext(examDetails);
			handlePostExamContextSwitchCallback?.(examDetails.exam.id);
		} else {
			showToast({
				message: "Failed to switch exam, please try again",
				duration: 500,
			});
		}
	};

	const handleClickAction = (type: "dismiss" | "switch-exam") => {
		if (type === "dismiss") {
			explicitCallTrackCustomEvent({
				eventName: events.appContextBannerDismissed,
			});
			setCache({ [examId]: { shouldShowBanner: false } });
		} else {
			explicitCallTrackCustomEvent({
				eventName: events.appContextBannerSwitchExamClicked,
			});
			if (hasMoreThanOneExam) {
				handleSwitchExam();
			} else {
				handleSwitchToSpecificExamContext();
			}
		}
	};

	return (
		<>
			<AppContextBanner
				visible={visible}
				font={appFont}
				title={bannerTitle}
				handleClickAction={handleClickAction}
				primaryCta={cta}
			/>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
					postExamContextSwitchCallback={handlePostExamContextSwitchCallback}
				/>
			)}
		</>
	);
};

export default AppContextBannerContainer;
